import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import numberOfAttendeesIcon from '../../../../assets/icons/numberOfAttendees.svg'
import { SessionsPerCampers } from '../../../../types/libertyLake'
import calendarIcon from '../../../../assets/icons/calendar.svg'
import pricingIcon from '../../../../assets/icons/dollar.svg'
import discountIcon from '../../../../assets/icons/discount.svg'
import {
  checkIfEarlyPayDiscount,
  checkIfMultiWeekDiscount,
  checkIfNewFamilyDiscount,
  checkIfOccupationalDiscount,
  checkIfPreSchoolDiscount,
  checkIfSiblingDiscount,
  checkIfTransportationDiscount,
  formatNumberWithCommas
} from '../../../../helpers/LibertyLake/helpers'

const Step4 = () => {
  const { sessionsPerCampers, familyInfo, pricingMap, addonsMap, siblingDiscountChart, libertyLakeData } = useSelector((state: RootState) => state.steps)

  const showDiscountAmounts = process.env.NODE_ENV === 'development'

  const discountAmounts = useMemo(() => {
    const multiWeek = sessionsPerCampers.reduce((sum, s) => sum + (s.multiWeekDiscount || 0), 0)

    const preSchoolPercentage = libertyLakeData?.Discount?.['Pre-School']?.['Pre-K, Kindergarten']?.Percentage ?? 0.05
    const preSchool = sessionsPerCampers.reduce((sum, s) => {
      if (s.grade !== 'Pre thru K') return sum
      const base = (s.weeks || []).reduce((weekSum, week) => weekSum + ((pricingMap && pricingMap[week]?.price) || 0), 0)
      const afterMultiWeek = base - (s.multiWeekDiscount || 0)
      return sum + Math.max(0, afterMultiWeek) * preSchoolPercentage
    }, 0)

    const sibling = (() => {
      if (!pricingMap || sessionsPerCampers.length < 2) return 0

      const camperPrices = sessionsPerCampers.map(s => {
        const base = (s.weeks || []).reduce((weekSum, week) => weekSum + ((pricingMap && pricingMap[week]?.price) || 0), 0)
        const afterMultiWeek = base - (s.multiWeekDiscount || 0)
        const afterPreSchool = s.grade === 'Pre thru K' ? Math.max(0, afterMultiWeek) * (1 - preSchoolPercentage) : afterMultiWeek
        return afterPreSchool
      })

      camperPrices.sort((a, b) => b - a)

      let total = 0
      camperPrices.forEach((price, index) => {
        const percentage = siblingDiscountChart?.[`${index + 1}`]?.Percentage ?? 0
        total += Math.max(0, price) * percentage
      })

      return total
    })()

    const transportation =
      (familyInfo.uniqueSessions?.length || 0) * (familyInfo.amTransportationDiscount || 0) +
      (familyInfo.uniqueSessions?.length || 0) * (familyInfo.pmTransportationDiscount || 0)

    const newFamily = familyInfo.newFamilyDiscount || 0

    const occupationalBase = Math.max(0, (familyInfo.familyPrice || 0) - transportation - newFamily)
    const occupational = occupationalBase * (familyInfo.occupationalDiscountPercentage || 0)

    const earlyBird = familyInfo.earlyPayDiscount || 0

    return {
      multiWeek,
      preSchool,
      sibling,
      newFamily,
      occupational,
      transportation,
      earlyBird
    }
  }, [familyInfo, libertyLakeData, pricingMap, sessionsPerCampers, siblingDiscountChart])

  const getAddonsCost = () => {
    let total = 0
    sessionsPerCampers.forEach(s => {
      let eachCamperAddonCost = 0
      s.addons.forEach(sAdd => {
        if (addonsMap) {
          eachCamperAddonCost += addonsMap[sAdd].price * s.weeks.length
        }
      })
      total += eachCamperAddonCost
    })

    return total
  }

  return <>
    <div className='step4'>
      <div className="summaryDetailsArea">
        <h3 className="summaryTitle">Summary</h3>
        <div className="familyInfoCard">
          <h3 className="cardTitle">Guardian's Information</h3>
          <div className="row">
            <label className='fieldLabel'><span>#</span>Name: {familyInfo.firstName} {familyInfo.lastName}</label>
          </div>
          <div className="row">
            <label className='fieldLabel'><span>#</span>Email: {familyInfo.email}</label>
          </div>
          <div className="row">
            <label className='fieldLabel'><span>#</span>Phone: {familyInfo.phone}</label>
          </div>
          {familyInfo.totalFamilyDiscount > 0 && (
            <>
              <div className="row">
                <label className='fieldLabel'><span>#</span>Discounts:</label>
              </div>
              {checkIfPreSchoolDiscount(sessionsPerCampers) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>Pre-School Discount{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.preSchool)})` : ''}
                  </label>
                </div>
              )}
              {checkIfMultiWeekDiscount(sessionsPerCampers) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>Multi-Week Discount{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.multiWeek)})` : ''}
                  </label>
                </div>
              )}
              {checkIfSiblingDiscount(sessionsPerCampers, siblingDiscountChart) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>Sibling Discount{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.sibling)})` : ''}
                  </label>
                </div>
              )}
              {checkIfNewFamilyDiscount(familyInfo) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>New Family{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.newFamily)})` : ''}
                  </label>
                </div>
              )}
              {checkIfOccupationalDiscount(familyInfo) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>{familyInfo.occupation}{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.occupational)})` : ''}
                  </label>
                </div>
              )}
              {checkIfTransportationDiscount(familyInfo) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>Transportation Discount{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.transportation)})` : ''}
                  </label>
                </div>
              )}
              {checkIfEarlyPayDiscount(familyInfo) && (
                <div className='row subRow'>
                  <label className='fieldLabel'>
                    <span>-</span>Early Bird Discount{showDiscountAmounts ? ` (-$${formatNumberWithCommas(discountAmounts.earlyBird)})` : ''}
                  </label>
                </div>
              )}
            </>
          )}
        </div>
        <div className="numberOfAttendees">
          <img src={numberOfAttendeesIcon} className="icon" />
          <label className='fieldLabel'>{sessionsPerCampers.length} Attendee(s)</label>
        </div>
        <div className="sessionsDetailsArea">
          {sessionsPerCampers.length && sessionsPerCampers.map((sessionsPerAttendee: SessionsPerCampers, i: number) => (
            <div className='attendeeDetailCard' key={`${i}:${sessionsPerAttendee.camperId}`}>
              <h3 className='cardTitle'>{sessionsPerAttendee.camperId}</h3>
              <div className="mainRow">
                <div className="column1">
                  <div className="row">
                    <label className='fieldLabel'><span>#</span>Grade: {sessionsPerAttendee.grade}</label>
                  </div>
                </div>
                <div className="column2">
                  <div className="sessionsWeeksContainer">
                    {sessionsPerAttendee.weeks.length && sessionsPerAttendee.weeks.map((week: string, j: number) => (
                      <div className="eachWeek" key={`${j}:${week}`}>
                        <img src={calendarIcon} className='icon' />
                        <label className='fieldName'>{week}</label>
                        <span>{(pricingMap && pricingMap[week.trim()].date) || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {sessionsPerAttendee.addons.length
                ? (
                  <>
                    <div style={{ height: '0px', width: '100%', border: '1px solid #F0F4EF', marginBottom: '10px' }} />
                    <div className="mainRow">
                      <div className="column1">
                        <div className="row">
                          <label className='fieldLabel'><span>#</span>Add-ons</label>
                        </div>
                      </div>
                      <div className="column2">
                        <div className="sessionsWeeksContainer">
                          {sessionsPerAttendee.addons.map((addon: string, j: number) => (
                            <div className="eachWeek" key={`${j}:${addon}`}>
                              <img src={calendarIcon} className='icon' />
                              <label className='fieldName'>{addon}</label>
                              <span>{(addonsMap && addonsMap[addon.trim()].date) || ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )
                : null}
            </div>
          ))}
        </div>
      </div>
      <div className="summaryAmountArea">
        <h3 className="pricingBreakDownTitle">Pricing Breakdown</h3>
        <div className='pricingColumn'>
          <div className="pricingRow">
            <label className='fieldLabel'>Sub Total</label>
            <div className="price">
              <img src={pricingIcon} className='pricingIcon' />
              <h3 className='subPrice'>${formatNumberWithCommas(familyInfo.familySessionsCost)}</h3>
            </div>
          </div>
          <div className="discountRow">
            <label className='fieldLabel'>Discount</label>
            <div className="price">
              <img src={discountIcon} className='discountIcon' />
              <h3 className='subPrice'>- ${formatNumberWithCommas(familyInfo.totalFamilyDiscount)}</h3>
            </div>
          </div>
          <div className="discountRow">
            <label className='fieldLabel'>Add-ons</label>
            <div className="price">
              <img src={pricingIcon} className='discountIcon' />
              <h3 className='subPrice'>+ ${formatNumberWithCommas(getAddonsCost())}</h3>
            </div>
          </div>
        </div>
        <div className="grandTotal">
          <h3 className='totalText'>Grand Total</h3>
          <h2 className="grandTotalPrice">${formatNumberWithCommas(familyInfo.grandTotalFamilyCost + getAddonsCost())}</h2>
        </div>
      </div>
    </div>
  </>
}

export default Step4
